import PropTypes from "prop-types";

const bulletToText = (bullet) =>
  typeof bullet === "string" ? bullet : String(bullet?.text || "").trim();

function renderBulletIcon(iconKey) {
  if (!iconKey) return null;
  const key = String(iconKey).trim().toLowerCase();
  const wrapperClass =
    "inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 shadow-sm";
  if (key === "star") {
    return (
      <span className={wrapperClass} aria-hidden="true">
        <svg className="h-2.5 w-2.5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 0 0 .95.69h4.158c.969 0 1.371 1.24.588 1.81l-3.363 2.444a1 1 0 0 0-.364 1.118l1.287 3.955c.3.921-.755 1.688-1.54 1.118l-3.364-2.444a1 1 0 0 0-1.175 0l-3.364 2.444c-.784.57-1.838-.197-1.539-1.118l1.287-3.955a1 1 0 0 0-.364-1.118L2.05 9.382c-.783-.57-.38-1.81.588-1.81h4.159a1 1 0 0 0 .95-.69l1.302-3.955Z" />
        </svg>
      </span>
    );
  }
  if (key === "document") {
    return (
      <span className={wrapperClass} aria-hidden="true">
        <svg
          className="h-2.5 w-2.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
          <path d="M8 13h8" />
          <path d="M8 17h8" />
        </svg>
      </span>
    );
  }
  if (key === "recurring") {
    return (
      <span className={wrapperClass} aria-hidden="true">
        <svg
          className="h-2.5 w-2.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 11a8 8 0 0 0-14.9-4" />
          <path d="M4 4v4h4" />
          <path d="M4 13a8 8 0 0 0 14.9 4" />
          <path d="M20 20v-4h-4" />
        </svg>
      </span>
    );
  }
  if (key === "health_flag") {
    return (
      <span className={wrapperClass} aria-hidden="true">
        <svg
          className="h-2.5 w-2.5"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8 14c3.5-2.2 5.2-4.7 5.2-7.6V3.7L8 2 2.8 3.7v2.7C2.8 9.3 4.5 11.8 8 14Z" />
          <path d="M8 5.3v3.9" />
          <path d="M6.1 7.25h3.8" />
        </svg>
      </span>
    );
  }
  if (key === "client_label") {
    return (
      <span className={wrapperClass} aria-hidden="true">
        <svg className="h-2.5 w-2.5" viewBox="0 0 16 16" fill="currentColor">
          <path d="M5.5 1.75A1.75 1.75 0 0 0 3.75 3.5v10a.5.5 0 0 0 .742.438L8 12.07l3.508 1.868a.5.5 0 0 0 .742-.438v-10A1.75 1.75 0 0 0 10.5 1.75h-5Z" />
        </svg>
      </span>
    );
  }
  if (key === "new_label") {
    return (
      <span
        className="inline-flex h-4 items-center rounded-full border border-slate-300 bg-white px-1.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-slate-700 shadow-sm"
        aria-hidden="true"
      >
        New
      </span>
    );
  }
  return null;
}

export default function HelpGuideSectionAccordion({
  section,
  isOpen,
  onToggle,
  showToggle = true,
  onGoToSection,
  sectionRef,
  isMobile = false,
}) {
  return (
    <section
      ref={sectionRef}
      id={`help-guide-card-${section.id}`}
      className="rounded-2xl border border-slate-200 bg-white p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
        {showToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-controls={`help-guide-section-${section.id}`}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            {isOpen ? "Hide" : "Show"}
          </button>
        ) : null}
      </div>

      {isOpen ? (
        <div id={`help-guide-section-${section.id}`} className="mt-3 space-y-3">
          {section.description ? (
            <p className="text-sm text-slate-600">{section.description}</p>
          ) : null}
          {Array.isArray(section.items) && section.items.length > 0 ? (
            <div className="space-y-3">
              {section.items.map((item, idx) => (
                <div
                  key={`${section.id}-item-${item.label}-${idx}`}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
                >
                  <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                  {item.body ? (
                    <p className="mt-1 text-sm text-slate-600">{item.body}</p>
                  ) : null}
                  {Array.isArray(item.details) && item.details.length > 0 ? (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
                      {item.details.map((detail) => (
                        <li key={`${section.id}-item-${item.label}-${detail}`}>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
          {Array.isArray(section.bullets) && section.bullets.length > 0 ? (
            <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
              {section.bullets.map((bullet, idx) => {
                const bulletText = bulletToText(bullet);
                const icon =
                  typeof bullet === "string"
                    ? null
                    : renderBulletIcon(bullet.icon);
                const key = `${section.id}-bullet-${idx}-${bulletText}`;
                return (
                  <li key={key}>
                    {icon ? (
                      <span className="inline-flex items-center gap-2">
                        {icon}
                        <span>{bulletText}</span>
                      </span>
                    ) : (
                      bulletText
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {Array.isArray(section.tips) && section.tips.length > 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Common Mistakes & Tips
              </p>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-700">
                {section.tips.map((tip) => (
                  <li key={`${section.id}-tip-${tip}`}>{tip}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {section.anchorId && onGoToSection ? (
            <button
              type="button"
              onClick={() => onGoToSection(section)}
              className={
                isMobile
                  ? "w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  : "rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              }
            >
              Go to section
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

HelpGuideSectionAccordion.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    bullets: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          icon: PropTypes.string,
        }),
      ])
    ),
    tips: PropTypes.arrayOf(PropTypes.string),
    items: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        body: PropTypes.string,
        details: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    anchorId: PropTypes.string,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  showToggle: PropTypes.bool,
  onGoToSection: PropTypes.func,
  sectionRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  isMobile: PropTypes.bool,
};
